import { signOut } from 'aws-amplify/auth'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import { User } from '@/application/domain/user'
import { getEnvironmentConfig } from '@/application/Environment/getEnvironmentConfig'
import { DefaultUser, SampleUser } from '@/application/Mocks/user'
import { useAnalytics } from '@/application/Providers/AnalyticsProvider'
import { swrConfig } from '@/config/swr/swrConfig'
import { getUserInfo, getUserInfoKey } from './getUserInfo'

export interface AuthResponse {
  user: User
  error: (() => void) | undefined
  signOut: () => Promise<void>
}

// If no user is logged in we return a user with empty fields
// It's expected that this will primarily be used inside protected routes
export const useAuth = (): AuthResponse => {
  const { mockAuth } = getEnvironmentConfig()

  if (mockAuth) {
    return { user: SampleUser(), error: undefined, signOut: Promise.resolve }
  }

  const { data, error, isLoading } = useSWR<User, () => void>(getUserInfoKey, getUserInfo, {
    ...swrConfig,
    shouldRetryOnError: false,
  })

  const router = useRouter()
  const analytics = useAnalytics()

  if (!data && isLoading) {
    return { user: DefaultUser(), error: undefined, signOut }
  }

  // this is a bit of a hack to clear the SWR cache when the user signs out
  // and causes the application to flash on logout
  // currently can't find a better way to do this
  const clearSWRCache = async () => {
    router.reload()
  }

  return {
    user: data ?? DefaultUser(),
    error,
    signOut: async () => {
      analytics.signOutUser()
      await signOut()
      await router.push('/')
      await clearSWRCache()
    },
  }
}
